import React, {useState, useEffect, useCallback, useRef} from 'react';
import _ from 'lodash';

import Grid from '@material-ui/core/Grid';
import CircularProgress from '@material-ui/core/CircularProgress';

import PortfolioExAnteV2 from "../PortfolioExAnteV2";
import {EX_ANTE_INFO_TEXT, EX_ANTE_STEP_NAME, OBJECT_TYPES} from "../../../../../RiskProfiling/constants";
import {TRANSACTION_TYPE_VALUES} from "../../../../constants";
import {totalTransactionAmount} from "../../../../utils";
import {makeStyles} from "@material-ui/core/styles";
import {getErrorMessage} from "../../../../../../utils/utils";
import BcaIconButton from "../../../../../../components/Buttons/BcaIconButton/BcaIconButton";
import ChartSectionBordered
  from "../../../../../CustomerDashboardV2/components_v2/ChartSectionBordered/ChartSectionBordered";
import InfoCard
  from "../../../../../RiskProfiling/components/StepContent/components/step/DynamicStep/components/InfoCard";
import ExAntePDFButton
  from "../../../../../RiskProfiling/components/StepContent/components/step/ExAnteStepV2/ExAntePDFButton";
import WarningTooltip from "../../../../../../components/WarningTooltip";


const TRADING_OBJECT_TYPE_TITLES = {
  [OBJECT_TYPES.TRADING]: 'Einmalanlage (K/V/T)',
  [OBJECT_TYPES.SAVINGS_PLAN]: 'Sparplan',
  [OBJECT_TYPES.PAYOUT_PLAN]: 'Entnahmeplan',
  [OBJECT_TYPES.SWITCH_PLAN]: 'Tauschplan',
}

const useStyles = makeStyles(() => ({
  pageHeader: {
    fontSize: 24,
  },
  loadingContainer: {
    width: '100%',
    height: 'calc(100svh - 320px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  errorContainer: {
    padding: '16px',
    color: '#d32f2f',
    textAlign: "center"
  },
}));


const getOneYearSavingsPlan = (exAnteData, isSell) => {
  return _.get(exAnteData, `${isSell ? '1.exit' : '5.initial'}.cost_base_value`, 0);
};

const getOneYearSavingsPlanTitle = (action, objectType) => {
  if (action === TRANSACTION_TYPE_VALUES.SELL) {
    return objectType === OBJECT_TYPES.PAYOUT_PLAN && 'Entnahmeplan';
  }
  if (objectType === OBJECT_TYPES.SWITCH_PLAN) {
    return 'Tauschplan';
  }
  return 'Sparplan';
};

const PDF_TOOLTIP = 'Enthält die Ex-Ante-Kosten aller Transaktionen und Depots dieses Vorgangs.'


/**
 * Displays Ex-Ante cost calculations for a single portfolio across all relevant object types.
 * Fetches data lazily on mount if not already available.
 */
function ExAnteCostsStep(props) {
  const {
    questions: [question],
    onAnswerChange,
    dataService,
  } = props;

  const objectTypes = question.types;
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const cancelledRef = useRef(false);
  const [expanded, setExpanded] = useState(
    () => Object.fromEntries(objectTypes.map(k => [k, true]))
  );

  const portfolio = question.portfolio;

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await dataService._fetchPortfolioExAnteData(portfolio, objectTypes);
      if (!cancelledRef.current) {
        question.dataFetched = true;
        onAnswerChange(question.uid, data);
      }
    } catch (e) {
      if (!cancelledRef.current) {
        setError(e);
      }
    } finally {
      if (!cancelledRef.current) {
        setLoading(false);
      }
    }
  }, [portfolio, objectTypes]);

  useEffect(() => {
    cancelledRef.current = false;
    if (!portfolio || question.dataFetched) return;
    fetchData();

    return () => { cancelledRef.current = true; };
  }, [portfolio]);

  if (!portfolio) {
    return null;
  }

  if (loading) {
    return (
      <div className={classes.loadingContainer}>
        <CircularProgress />
      </div>
    );
  }

  if (error) {
    return (
      <div className={classes.errorContainer}>
        {getErrorMessage(error) || 'Fehler bei der Ex-Ante Berechnung'}
        <BcaIconButton
          icon={<i className={'fa fa-refresh'} />}
          tooltip={'Erneut versuchen'}
          onClick={fetchData}
        />
      </div>
    );
  }

  const portfolioExAnte = question.answer;

  if (!portfolioExAnte) {
    return null;
  }

  const getTotalAmount = (transactionType, objectType) => {
    if (objectType !== OBJECT_TYPES.TRADING) return undefined;

    const {transactions} = portfolio;
    return [transactionType, TRANSACTION_TYPE_VALUES.SWITCH].reduce((total, key) => {
      return total + totalTransactionAmount(transactions[key]);
    }, 0);
  };

  const renderObjectType = (objectType) => {
    const exAnteData = portfolioExAnte[objectType];

    if (!exAnteData || _.every(Object.keys(exAnteData), (key) => _.isEmpty(exAnteData[key]))) {
      return null;
    }

    const isPaymentPlan = [OBJECT_TYPES.PAYOUT_PLAN, OBJECT_TYPES.SWITCH_PLAN, OBJECT_TYPES.SAVINGS_PLAN]
      .includes(objectType);

    return (
      <Grid item xs={12}>
        <ChartSectionBordered
          title={<b>{TRADING_OBJECT_TYPE_TITLES[objectType]}</b>}
          expanded={expanded[objectType]}
          onExpanded={(newValue) => setExpanded(prevState => ({...prevState, [objectType]: newValue}))}
          content={
            <Grid container key={objectType}>
              {Object.keys(exAnteData).map(key => (
                <Grid item xs={12} key={key}>
                  <PortfolioExAnteV2
                    portfolioName={portfolio.data.name}
                    portfolioCategory={portfolio.categoryName}
                    totalValue={getTotalAmount(key, objectType)}
                    exAnteData={exAnteData[key]}
                    transactionType={key}
                    oneYearSavingsPlan={
                      isPaymentPlan
                      && ((key === 'sell' && objectType === OBJECT_TYPES.PAYOUT_PLAN)
                        || (key === 'buy' && objectType !== OBJECT_TYPES.PAYOUT_PLAN))
                      && getOneYearSavingsPlan(exAnteData[key], objectType === OBJECT_TYPES.PAYOUT_PLAN)
                    }
                    oneYearSavingsPlanTitle={getOneYearSavingsPlanTitle(key, objectType)}
                    isPrivateInvestment={_.get(portfolio, 'data.is_private_investment')}
                    simulationYears={
                      (key === 'sell' && [OBJECT_TYPES.PAYOUT_PLAN, OBJECT_TYPES.SWITCH_PLAN].includes(objectType)) ? 1 : 5
                    }
                  />
                </Grid>
              ))}
            </Grid>
          }
        />
      </Grid>
    );
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Grid container spacing={2}>
          <Grid item xs className={classes.pageHeader}><b>{EX_ANTE_STEP_NAME}</b></Grid>
          {question.withPrint && (
            <Grid item xs={'auto'}>
              <ExAntePDFButton
                dataService={dataService}
                btnText={<>
                  Ex-Ante-Gesamt-PDF herunterladen<WarningTooltip title={PDF_TOOLTIP} />
                </>}
              />
            </Grid>
          )}
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <InfoCard uid={question.uid} info_text={EX_ANTE_INFO_TEXT} isNewDesign />
      </Grid>
      {objectTypes.map(renderObjectType)}
    </Grid>
  );
}

export default ExAnteCostsStep;
